import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import * as authService from '../services/authService'
import { fetchSubscription } from '../services/billingApi'
import { getUserFacingErrorMessage } from '../utils/userFacingError'
import {
  AUTH_REFRESH_TOKEN_STORAGE_KEY,
  AUTH_TOKEN_STORAGE_KEY,
  persistTokens,
  setSessionInvalidHandler,
} from '../services/api'
import { syncAdminSentryUser } from '../lib/sentry'
import {
  AuthActionType,
  authReducer,
  createInitialAuthState,
} from './reducers/authReducer'

const STORAGE_USER = 'shopora_store_admin_user'
const STORAGE_TOKEN = AUTH_TOKEN_STORAGE_KEY
const STORAGE_REFRESH = AUTH_REFRESH_TOKEN_STORAGE_KEY
const STORE_BUSINESS_ROLES = new Set(['org_admin', 'staff'])

const AuthContext = createContext<any>(null)

function readStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_USER)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(
    authReducer,
    undefined,
    () => createInitialAuthState(readStoredUser),
  )

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_TOKEN)
    if (state.user && !token) {
      localStorage.removeItem(STORAGE_USER)
      dispatch({ type: AuthActionType.SET_USER, payload: null })
    }
  }, [state.user])

  useEffect(() => {
    if (state.user) syncAdminSentryUser(state.user)
  }, [state.user])

  const login = useCallback(async (credentials) => {
    dispatch({ type: AuthActionType.LOGIN_START })
    try {
      const { user: u, token, refreshToken } = await authService.login(credentials)
      if (!STORE_BUSINESS_ROLES.has(u.role)) {
        const err = new Error('This panel is for store business accounts only.')
        err.code = 'FORBIDDEN_ROLE'
        throw err
      }
      if (refreshToken) {
        persistTokens(token, refreshToken)
      } else {
        localStorage.setItem(STORAGE_TOKEN, token)
        localStorage.removeItem(STORAGE_REFRESH)
      }
      localStorage.setItem(STORAGE_USER, JSON.stringify(u))
      dispatch({ type: AuthActionType.LOGIN_SUCCESS, payload: u })
      syncAdminSentryUser(u)
      return u
    } catch (e) {
      dispatch({
        type: AuthActionType.LOGIN_FAILURE,
        payload: getUserFacingErrorMessage(e, 'Login failed', { loginAttempt: true }),
      })
      throw e
    }
  }, [])

  const startSupportAccess = useCallback(async (launchToken: string) => {
    dispatch({ type: AuthActionType.LOGIN_START })
    try {
      const { user: u, token } = await authService.exchangeSupportAccess(launchToken)
      localStorage.removeItem(STORAGE_REFRESH)
      localStorage.setItem(STORAGE_TOKEN, token)
      localStorage.setItem(STORAGE_USER, JSON.stringify(u))
      dispatch({ type: AuthActionType.LOGIN_SUCCESS, payload: u })
      syncAdminSentryUser(u)
      return u
    } catch (e) {
      dispatch({
        type: AuthActionType.LOGIN_FAILURE,
        payload: getUserFacingErrorMessage(e, 'Support access failed'),
      })
      throw e
    }
  }, [])

  const logout = useCallback(async () => {
    dispatch({ type: AuthActionType.LOGOUT_START })
    const refresh = localStorage.getItem(STORAGE_REFRESH)
    // Clear local session first so protected UI redirects immediately and
    // concurrent API calls stop attaching a dead access token.
    localStorage.removeItem(STORAGE_TOKEN)
    localStorage.removeItem(STORAGE_REFRESH)
    localStorage.removeItem(STORAGE_USER)
    localStorage.removeItem('shopora_store_admin_stores')
    localStorage.removeItem('shopora_store_admin_current_store_id')
    syncAdminSentryUser(null)
    dispatch({ type: AuthActionType.LOGOUT_COMPLETE })
    try {
      await authService.logout(refresh)
    } catch {
      /* best-effort revoke */
    }
  }, [])

  useEffect(() => {
    setSessionInvalidHandler(() => {
      void logout()
    })
    return () => setSessionInvalidHandler(null)
  }, [logout])

  const completePasswordChange = useCallback(async (payload) => {
    dispatch({ type: AuthActionType.LOGIN_START })
    try {
      await authService.changePassword(payload)
      const current = readStoredUser()
      if (!current) {
        const message = 'Session expired. Please sign in again.'
        dispatch({ type: AuthActionType.LOGIN_FAILURE, payload: message })
        throw new Error(message)
      }
      const nextUser = { ...current, mustChangePassword: false }
      localStorage.setItem(STORAGE_USER, JSON.stringify(nextUser))
      dispatch({ type: AuthActionType.LOGIN_SUCCESS, payload: nextUser })
      return nextUser
    } catch (e) {
      dispatch({
        type: AuthActionType.LOGIN_FAILURE,
        payload: getUserFacingErrorMessage(e, 'Could not update password.'),
      })
      throw e
    }
  }, [])

  const refreshBillingStatus = useCallback(async () => {
    const current = readStoredUser()
    if (!current) return null
    try {
      const sub = await fetchSubscription()
      const billingRequired = Boolean(sub?.billingRequired)
      const nextUser = { ...current, billingRequired }
      localStorage.setItem(STORAGE_USER, JSON.stringify(nextUser))
      dispatch({ type: AuthActionType.SET_USER, payload: nextUser })
      return nextUser
    } catch {
      return current
    }
  }, [])

  const clearError = useCallback(() => {
    dispatch({ type: AuthActionType.CLEAR_ERROR })
  }, [])

  const value = useMemo(
    () => ({
      user: state.user,
      isAuthenticated: Boolean(state.user),
      isSupportSession: Boolean(state.user?.supportSession),
      login,
      startSupportAccess,
      logout,
      loading: state.loading,
      error: state.error,
      clearError,
      completePasswordChange,
      refreshBillingStatus,
    }),
    [
      state.user,
      state.loading,
      state.error,
      login,
      startSupportAccess,
      logout,
      clearError,
      completePasswordChange,
      refreshBillingStatus,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
